import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useWallet } from "@/context/WalletContext";
import { createApiService, apiService } from "@/App";
import { toast } from "sonner";
import Header from "@/components/Header";
import { 
  Bot, 
  ArrowLeft,
  Search,
  TrendingUp,
  Heart,
  Copy,
  Sparkles,
  Filter,
  Trophy,
  Zap,
  Plus
} from "lucide-react";

const STXOSHI_LOGO = process.env.REACT_APP_LOGO_URL;

export default function Explore() {
  const navigate = useNavigate();
  const { isConnected, getAuthHeaders } = useWallet();
  const authenticatedApi = createApiService(getAuthHeaders);
  
  const [agents, setAgents] = useState([]);
  const [predictionBots, setPredictionBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("likes_count");
  const [showAllAgents, setShowAllAgents] = useState(false);
  const [showAllBots, setShowAllBots] = useState(false);

  useEffect(() => {
    fetchAgents();
    fetchBots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy]);

  const fetchAgents = async () => {
    try {
      const res = await apiService.exploreAgents(sortBy);
      setAgents(res.data);
    } catch (err) {
      console.error("Failed to fetch agents:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBots = async () => {
    try {
      // Fetch all public prediction bots (no auth required for explore)
      const res = await apiService.get('/prediction-bots/public');
      if (res.data.success) {
        setPredictionBots(res.data.bots || []);
      }
    } catch (err) {
      console.error("Failed to fetch prediction bots:", err);
      // If public endpoint doesn't exist, try authenticated
      if (isConnected) {
        try {
          const authRes = await authenticatedApi.getBots();
          if (authRes.data.success) {
            setPredictionBots(authRes.data.bots || []);
          }
        } catch (authErr) {
          console.error("Failed to fetch bots (auth):", authErr);
        }
      }
    }
  };

  const handleClone = async (agentId) => {
    if (!isConnected) {
      toast.error("Please connect your wallet");
      return;
    }
    try {
      const res = await authenticatedApi.cloneAgent(agentId);
      toast.success("Agent cloned successfully!");
      navigate(`/agent/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to clone agent");
    }
  };

  const handleLike = async (agentId) => {
    if (!isConnected) {
      toast.error("Please connect your wallet");
      return;
    }
    try {
      await authenticatedApi.likeAgent(agentId);
      fetchAgents();
    } catch (err) {
      if (err.response?.status === 400) {
        await authenticatedApi.unlikeAgent(agentId);
        fetchAgents();
      }
    }
  };

  const filteredAgents = agents.filter(agent => 
    agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    agent.config?.archetype?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Filter bots by search query
  const filteredBots = predictionBots.filter(bot => {
    const searchLower = searchQuery.toLowerCase();
    const botName = (bot.name || '').toLowerCase();
    const botType = (bot.bot_type || bot.strategy || '').toLowerCase();
    return (
      botName.includes(searchLower) ||
      botType.includes(searchLower) ||
      `bot ${botName}`.includes(searchLower)
    );
  });

  // Sort bots based on selected sort criteria
  const sortedFilteredBots = [...filteredBots].sort((a, b) => {
    switch (sortBy) {
      case 'likes_count':
        // For bots, use total_trades as a proxy for popularity
        return (b.total_trades || 0) - (a.total_trades || 0);
      case 'total_pnl':
        return (b.total_pnl_pct || 0) - (a.total_pnl_pct || 0);
      case 'trust_score':
        // For bots, use status as proxy (running bots = more trusted)
        const statusOrder = { running: 3, stopped: 2, killed: 1 };
        return (statusOrder[b.status] || 0) - (statusOrder[a.status] || 0);
      default:
        return (b.total_pnl_pct || 0) - (a.total_pnl_pct || 0);
    }
  });

  const getRiskColor = (archetype) => {
    if (archetype?.includes('Conservative') || archetype?.includes('Swiss')) return 'text-green-400';
    if (archetype?.includes('Yield') || archetype?.includes('Arbitrage')) return 'text-yellow-400';
    return 'text-primary';
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Main Content */}
      <main className="max-w-6xl mx-auto p-6">
        {/* Search & Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search agents by name or strategy..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-muted/50"
            />
          </div>
          <Tabs value={sortBy} onValueChange={setSortBy} className="w-auto">
            <TabsList className="bg-muted/50">
              <TabsTrigger value="likes_count" className="text-xs">
                <Heart className="w-3 h-3 mr-1" />
                Popular
              </TabsTrigger>
              <TabsTrigger value="total_pnl" className="text-xs">
                <TrendingUp className="w-3 h-3 mr-1" />
                Top P&L
              </TabsTrigger>
              <TabsTrigger value="trust_score" className="text-xs">
                <Sparkles className="w-3 h-3 mr-1" />
                Trust
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* ARBITRAGE BOTS Section */}
        {sortedFilteredBots.length > 0 && (
          <Card className="glass-card border-secondary/30 mb-8">
            <CardHeader className="border-b border-border bg-secondary/5 px-3 py-3">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="font-heading text-lg tracking-wider flex items-center gap-2">
                  <Zap className="w-5 h-5 text-secondary" />
                  BOTS
                  <Badge variant="outline" className="text-secondary border-secondary/50 text-xs">
                    {sortedFilteredBots.length}
                  </Badge>
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => navigate('/create?type=bot')}
                  className="border-secondary/50 text-secondary hover:bg-secondary/10 px-2"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  New
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {sortedFilteredBots
                  .slice(0, showAllBots ? sortedFilteredBots.length : 5)
                  .map((bot) => (
                  <div 
                    key={bot.id}
                    className="p-4 hover:bg-muted/30 transition-colors cursor-pointer"
                    onClick={() => navigate(`/bot/${bot.id}`)}
                    data-testid={`explore-bot-row-${bot.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          bot.status === 'running' ? 'bg-green-500/10' : 'bg-secondary/10'
                        }`}>
                          <Zap className={`w-6 h-6 ${
                            bot.status === 'running' ? 'text-green-400' : 'text-secondary'
                          }`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold">{bot.name ? `Bot ${bot.name}` : 'Arbitrage Bot'}</h3>
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${
                                bot.status === 'running' 
                                  ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                                  : bot.status === 'killed'
                                    ? 'bg-red-500/20 text-red-400 border-red-500/30'
                                    : 'bg-muted text-muted-foreground'
                              }`}
                            >
                              {bot.status === 'running' && (
                                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse mr-1" />
                              )}
                              {bot.status?.toUpperCase()}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Balance: {((bot.total_balance_stxoshi || 0) / 1_000_000).toLocaleString()} STXOSHI
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className={`font-mono font-semibold ${
                            (bot.total_pnl_pct || 0) >= 0 ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {(bot.total_pnl_pct || 0) >= 0 ? '+' : ''}{(bot.total_pnl_pct || 0).toFixed(2)}%
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {bot.total_trades || 0} trades
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {sortedFilteredBots.length > 5 && (
                  <div className="p-3 text-center">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setShowAllBots(!showAllBots)}
                      data-testid="view-all-bots-btn"
                    >
                      {showAllBots ? 'Show less' : `View all ${sortedFilteredBots.length} bots`}
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* TREASURY AGENTS Section - Matches Bots layout exactly */}
        <Card className="glass-card border-primary/30">
          <CardHeader className="border-b border-border bg-primary/5 px-3 py-3">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="font-heading text-lg tracking-wider flex items-center gap-2">
                <Bot className="w-5 h-5 text-primary" />
                AGENTS
                <Badge variant="outline" className="text-primary border-primary/50 text-xs">
                  {filteredAgents.length}
                </Badge>
              </CardTitle>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/create')}
                className="border-primary/50 text-primary hover:bg-primary/10 px-2"
              >
                <Plus className="w-4 h-4 mr-1" />
                New
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Loading agents...</div>
            ) : filteredAgents.length === 0 ? (
              <div className="p-12 text-center">
                <Bot className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                <h3 className="font-heading text-lg mb-2">No Agents Found</h3>
                <p className="text-muted-foreground mb-4">
                  Be the first to create a public agent!
                </p>
                <Button onClick={() => navigate('/create')}>Create Agent</Button>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {/* Sort by P/L high to low */}
                {[...filteredAgents]
                  .sort((a, b) => (b.total_pnl || 0) - (a.total_pnl || 0))
                  .slice(0, showAllAgents ? filteredAgents.length : 5)
                  .map(agent => (
                  <div 
                    key={agent.id}
                    className="p-4 hover:bg-muted/30 transition-colors cursor-pointer"
                    onClick={() => navigate(`/agent/${agent.id}`)}
                    data-testid={`explore-agent-${agent.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                          <Bot className="w-6 h-6 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold">{agent.name}</h3>
                            <Badge variant="outline" className="text-xs font-mono">
                              Score: {agent.trust_score}
                            </Badge>
                            <span className={`text-xs ${getRiskColor(agent.config?.archetype)}`}>
                              {agent.config?.archetype}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground font-mono">
                            {agent.sbtc_balance?.toFixed(6)} sBTC
                          </p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        {/* Show DeFi yield - prioritize STXOSHI, then other protocols */}
                        {(agent.total_stxoshi_yield || 0) > 0 ? (
                          <>
                            <p className="font-mono font-semibold text-green-400">
                              +{agent.total_stxoshi_yield.toLocaleString(undefined, {maximumFractionDigits: 4})}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {agent.total_stxoshi_value?.toLocaleString(undefined, {maximumFractionDigits: 2})} STXOSHI
                            </p>
                          </>
                        ) : (agent.total_defi_yield || 0) > 0 && agent.dominant_asset ? (
                          <>
                            <p className="font-mono font-semibold text-green-400">
                              +{agent.total_defi_yield.toLocaleString(undefined, {maximumFractionDigits: 4})}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {agent.total_defi_value?.toLocaleString(undefined, {maximumFractionDigits: 2})} {agent.dominant_asset}
                            </p>
                          </>
                        ) : (agent.total_yield_earned || agent.total_pnl || 0) > 0 ? (
                          <>
                            <p className="font-mono font-semibold text-green-400">
                              +{(agent.total_yield_earned || agent.total_pnl || 0).toFixed(4)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {agent.stx_balance?.toFixed(2)} STX
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="font-mono font-semibold">{agent.sbtc_balance?.toFixed(4) || '0.0000'}</p>
                            <p className="text-xs text-muted-foreground">
                              {agent.stx_balance?.toFixed(2)} STX
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {filteredAgents.length > 5 && (
                  <div className="p-3 text-center">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setShowAllAgents(!showAllAgents)}
                      data-testid="view-all-agents-btn"
                    >
                      {showAllAgents ? 'Show less' : `View all ${filteredAgents.length} agents`}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
